import React from "react";
import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import { Chart } from "primereact/chart";
import { barGraphOptionsFunding } from "app/uicomponents/macrocomponents/PlansSummary/config";

export interface ColumnDef {
  label: string;
  cell: (row: any) => React.ReactNode;
  cellStyle?: (row: any) => React.CSSProperties | undefined;
  total?: (rows: any[]) => React.ReactNode;
}

export interface ChartDataset {
  backgroundColor: string;
  data: number[];
  label: string;
}

interface IRCFundingPageProps {
  rows: any[];
  columns: ColumnDef[];
  chartLabels: (string | number)[];
  chartDatasets: ChartDataset[];
  pageNumber: string | number;
  title?: string;
  /** "charts" renders only the bar chart; "table" renders only the data table */
  mode: "charts" | "table";
}

const IRCFundingPage = ({
  rows,
  columns,
  chartLabels,
  chartDatasets,
  pageNumber,
  title = "Plan Funding",
  mode,
}: IRCFundingPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { ipTooltipColor, secondaryColor } = getThemeColors(branding);

  const colorVariations = getColorVariations(secondaryColor, columns.length).reverse();

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          {title}
        </h1>

        {mode === "charts" && (
          <div style={{ width: "503px" }}>
            <Chart
              type="bar"
              data={{ labels: chartLabels, datasets: chartDatasets }}
              options={barGraphOptionsFunding(false, ipTooltipColor, "Plan Year", "", "Plan Year")}
              width="503px"
              height="420px"
            />
            <div className="flex flex-wrap justify-center gap-3 mt-3">
              {chartDatasets.map(({ backgroundColor, label }, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor }} />
                  <span style={{ color: "#000000", fontSize: "12px" }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {mode === "table" && (
          <table className="w-full border-collapse">
            <thead className="text-white">
              <tr>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    className="p-1 text-center pdf_table_border border-white font-semibold"
                    style={{ backgroundColor: colorVariations[i], fontSize: "9px" }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "9px" }}
                >
                  {columns.map((col, j) => (
                    <td
                      key={j}
                      className="p-1 text-center pdf_table_border border-white"
                      style={col.cellStyle?.(row)}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr
                style={{
                  backgroundColor: "#D1D5DB",
                  color: "#000000",
                  fontSize: "9px",
                  fontWeight: 600,
                }}
              >
                {columns.map((col, i) => (
                  <td key={i} className="p-1 text-center pdf_table_border border-white">
                    {col.total ? col.total(rows) : i === 0 ? "Total" : null}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCFundingPage;
